import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Globe, HeartHandshake } from "lucide-react";
import Logo from "./Logo";
import { useLanguage } from "../contexts/LanguageContext";

const NAV_ITEMS = [
  { to: "/", key: "home" },
  { to: "/about", key: "about" },
  { to: "/impact", key: "impact" },
  { to: "/programs", key: "programs" },
  { to: "/leadership", key: "leadership" },
  { to: "/membership", key: "membership" },
  { to: "/get-involved", key: "getInvolved" },
  { to: "/contact", key: "contact" },
];

export default function Header() {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl shadow-sm border-b border-black/5"
          : "bg-white/60 backdrop-blur-md"
      }`}
    >
      <div className="gk-container flex items-center justify-between h-16 sm:h-20">
        <Link to="/" aria-label="Home" data-testid="header-logo-link">
          <Logo />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.key}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium font-heading rounded-full transition-colors ${
                  isActive
                    ? "text-[#0B5D1E] bg-[#0B5D1E]/8"
                    : "text-neutral-700 hover:text-[#0B5D1E]"
                }`
              }
            >
              {t.nav[item.key]}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {/* Lang toggle */}
          <button
            type="button"
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "en" ? "fr" : "en")}
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold font-heading text-neutral-700 hover:text-[#0B5D1E] hover:bg-[#0B5D1E]/8 transition-colors"
            aria-label="Toggle language"
          >
            <Globe size={14} />
            <span>{lang === "en" ? "EN" : "FR"}</span>
            <span className="text-neutral-400">/</span>
            <span className="text-neutral-400">{lang === "en" ? "FR" : "EN"}</span>
          </button>

          <Link
            to="/get-involved"
            data-testid="header-donate-btn"
            className="hidden sm:inline-flex items-center gap-2 bg-[#B5651D] hover:bg-[#D48851] text-white text-sm font-semibold font-heading px-5 py-2.5 rounded-full transition-all hover:-translate-y-0.5 hover:shadow-lg"
          >
            <HeartHandshake size={16} />
            {t.nav.donate}
          </Link>

          <button
            type="button"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 rounded-full hover:bg-black/5"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-black/5 shadow-lg"
        >
          <nav className="gk-container py-4 flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-${item.key}`}
                className={({ isActive }) =>
                  `px-4 py-3 text-sm font-semibold font-heading rounded-xl transition-colors ${
                    isActive
                      ? "text-[#0B5D1E] bg-[#0B5D1E]/8"
                      : "text-neutral-800 hover:bg-neutral-100"
                  }`
                }
              >
                {t.nav[item.key]}
              </NavLink>
            ))}
            <div className="flex items-center gap-2 pt-3 mt-2 border-t border-black/5">
              <button
                type="button"
                data-testid="mobile-lang-toggle"
                onClick={() => setLang(lang === "en" ? "fr" : "en")}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full text-xs font-semibold bg-neutral-100 hover:bg-neutral-200"
              >
                <Globe size={14} />
                {lang === "en" ? "Français" : "English"}
              </button>
              <Link
                to="/get-involved"
                data-testid="mobile-donate-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#B5651D] text-white text-sm font-semibold font-heading px-4 py-2.5 rounded-full"
              >
                <HeartHandshake size={16} />
                {t.nav.donate}
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
